/**
 * 弁当予約管理画面
 * 店舗側で予約の確認、ステータス更新、統計表示を行う
 */

import React, { useState, useEffect, useRef } from 'react';
import { Card, Table, Button, Tag, Modal, Statistic, Row, Col, message, Space, Alert, Tooltip, Switch, TimePicker, Badge } from 'antd';
import { Printer, RefreshCw, Clock, Package, CheckCircle, AlertCircle, Coffee, Home } from 'lucide-react';
import api from '../../services/api';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';

import PageContainer from '../layout/PageContainer';
interface BentoOrder {
    id: number;
    orderNumber: string;
    customerName: string;
    phone: string;
    pickupTime: string;
    items: Array<{
        name: string;
        quantity: number;
        price: number;
    }>;
    totalAmount: number;
    quantityTotal: number;
    memo?: string;
    createdAt: string;
    status: string;
    picked_up?: number;
    picked_up_at?: string;
    needs_phone_confirmation?: number;
    lineUserId?: string;
}

interface OrderStats {
    totalOrders: number;
    totalQuantity: number;
    totalAmount: number;
    statusBreakdown: {
        pending: number;
        confirmed: number;
        preparing: number;
        ready: number;
        completed: number;
        cancelled: number;
    };
    nextPickupTime: string | null;
}

const BentoReservationManager: React.FC = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState<BentoOrder[]>([]);
    const [stats, setStats] = useState<OrderStats | null>(null);
    const [loading, setLoading] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<BentoOrder | null>(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(true);
    const [todayOpenTime, setTodayOpenTime] = useState('11:00');
    
    const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

    // データ取得
    const fetchData = async () => {
        setLoading(true);
        try {
            // 注文一覧取得
            const ordersResponse = await api.get('/bento-orders/today');
            console.log('Orders API Response:', ordersResponse.data);
            if (ordersResponse.data.success) {
                console.log('Setting orders:', ordersResponse.data.orders);
                const normalizedOrders = (ordersResponse.data.orders || []).map((order: any) => {
                    const orderId = order.id ?? order.order_id ?? order.orderId;
                    return {
                        ...order,
                        id: orderId,
                        key: orderId
                    };
                });
                setOrders(normalizedOrders);
            }
            
            // 統計情報取得
            const statsResponse = await api.get('/bento-orders/stats');
            console.log('Stats API Response:', statsResponse.data);
            if (statsResponse.data.success) {
                console.log('Setting stats:', statsResponse.data.stats);
                setStats(statsResponse.data.stats);
            }
            
            // 設定取得
        } catch (error) {
            console.error('データ取得エラー:', error);
            message.error('データの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    // 開店時間取得
    const fetchTodayOpenTime = async () => {
        try {
            const response = await api.get('/bento-orders/today-open-time');
            if (response.data.success) {
                setTodayOpenTime(response.data.data.openTime);
            }
        } catch (error) {
            console.error('開店時間取得エラー:', error);
        }
    };

    // 開店時間変更ハンドラ
    const handleOpenTimeChange = async (value: string) => {
        try {
            const response = await api.put('/bento-orders/today-open-time', {
                openTime: value
            });

            if (response.data.success) {
                message.success('開店時間を更新しました');
                setTodayOpenTime(value);
            }
        } catch (error) {
            console.error('開店時間更新エラー:', error);
            message.error('開店時間の更新に失敗しました');
        }
    };

    // 自動更新
    useEffect(() => {
        fetchData();
        fetchTodayOpenTime();
        
        if (autoRefresh) {
            refreshIntervalRef.current = setInterval(fetchData, 30000); // 30秒ごと
        }
        
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [autoRefresh]);

    // ステータス更新
    const handleStatusUpdate = async (orderId: number, newStatus: string) => {
        if (!orderId) {
            message.error('注文IDが取得できませんでした');
            return;
        }
        try {
            const response = await api.put(`/bento-orders/${orderId}/status`, {
                status: newStatus
            });
            
            if (response.data.success) {
                message.success('ステータスを更新しました');
                fetchData();
            }
        } catch (error) {
            console.error('ステータス更新エラー:', error);
            message.error('ステータス更新に失敗しました');
        }
    };

    // 設定更新

    // 朝の一括印刷
    const handleMorningPrint = async () => {
        try {
            const response = await api.get('/bento-orders/print/morning');
            
            if (response.data.success && response.data.pages) {
                const pages = response.data.pages;
                
                if (pages.length === 0) {
                    message.info('本日の予約はありません');
                    return;
                }
                
                // 各ページを順番に印刷
                for (let i = 0; i < pages.length; i++) {
                    const printWindow = window.open('', '_blank');
                    if (printWindow) {
                        printWindow.document.write(pages[i]);
                        printWindow.document.close();
                        
                        // 少し待機してから次の印刷
                        await new Promise(resolve => setTimeout(resolve, 1000));
                        
                        printWindow.onafterprint = () => {
                            printWindow.close();
                        };
                    }
                }
                
                message.success(`${pages.length}件の予約票を印刷しました`);
            }
        } catch (error) {
            console.error('印刷エラー:', error);
            message.error('印刷に失敗しました');
        }
    };

    // 個別印刷
    const handlePrintOrder = async (orderId: number) => {
        if (!orderId) {
            message.error('注文IDが取得できませんでした');
            return false;
        }
        try {
            const response = await api.get(`/bento-orders/print/order/${orderId}`);
            
            if (response.data.success) {
                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(response.data.html);
                    printWindow.document.close();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                }
                
                message.success('予約票を印刷しました');
                return true;
            }
            message.error('印刷に失敗しました');
            return false;
        } catch (error) {
            console.error('印刷エラー:', error);
            message.error('印刷に失敗しました');
            return false;
        }
    };

    // 手動受取処理
    const handleManualPickup = async (orderId: number) => {
        console.log('handleManualPickup called with ID:', orderId);
        if (!orderId) {
            message.error('注文IDが取得できませんでした');
            return;
        }
        try {
            const statusResponse = await api.put(`/bento-orders/${orderId}/status`, {
                status: 'completed'
            });
            if (!statusResponse.data.success) {
                message.error('ステータス更新に失敗しました');
                return;
            }

            const pickupResponse = await api.patch(`/bento-orders/${orderId}/pickup`);
            if (pickupResponse.data.success) {
                message.success('受取済みに更新しました');
                fetchData(); // 再読み込み
            } else {
                message.error('受取ステータスの更新に失敗しました');
            }
        } catch (error) {
            console.error('受取ステータス更新エラー:', error);
            message.error('受取ステータスの更新に失敗しました');
        }
    };

    const handlePrintAndConfirm = async (orderId: number) => {
        const printed = await handlePrintOrder(orderId);
        if (!printed) return;
        await handleStatusUpdate(orderId, 'confirmed');
    };

    const getDisplayStatus = (record: BentoOrder) => {
        const status = record.status;
        const pickedUp = (record as any).picked_up === 1;

        if (status === 'cancelled') return 'cancelled';
        if (status === 'completed' || status === 'picked_up' || pickedUp) return 'completed';
        if (status === 'confirmed' || status === 'preparing' || status === 'ready') return 'confirmed';
        if (status === 'pending') return 'pending';
        return status || 'pending';
    };

    const getStatusBadge = (record: BentoOrder) => {
        const displayStatus = getDisplayStatus(record);

        switch (displayStatus) {
            case 'pending':
                return (
                    <Tag color="red" style={{ fontWeight: 700, animation: 'bento-blink 1.2s ease-in-out infinite' }}>
                        🚨 受付待ち
                    </Tag>
                );
            case 'confirmed':
                return (
                    <Tag color="blue">
                        👨‍🍳 受付済
                    </Tag>
                );
            case 'completed':
                return (
                    <Tag color="default">
                        ✅ 受渡済
                    </Tag>
                );
            case 'cancelled':
                return (
                    <Tag color="default" style={{ textDecoration: 'line-through', color: '#999' }}>
                        🚫 キャンセル
                    </Tag>
                );
            default:
                return (
                    <Tag color="default">
                        {displayStatus}
                    </Tag>
                );
        }
    };

    // フィルタリング & ソート
    const getOrderId = (record: any) => record.id ?? record.order_id ?? record.orderId;

    const getStatusPriority = (record: BentoOrder) => {
        const displayStatus = getDisplayStatus(record);
        switch (displayStatus) {
            case 'pending':
                return 0;
            case 'confirmed':
                return 1;
            default:
                return 2;
        }
    };

    const getFilteredOrders = () => {
        const sortedOrders = [...orders].sort((a, b) => {
            const aPriority = getStatusPriority(a);
            const bPriority = getStatusPriority(b);
            if (aPriority !== bPriority) return aPriority - bPriority;

            const aTime = (a as any).pickup_time || (a as any).pickupTime || '';
            const bTime = (b as any).pickup_time || (b as any).pickupTime || '';
            return aTime.localeCompare(bTime);
        });
        return sortedOrders;
    };

    // テーブルカラム
    const columns = [
        {
            title: '予約番号',
            dataIndex: 'order_number',
            key: 'orderNumber',
            width: 100,
            render: (text: string) => <strong>{text}</strong>
        },
        {
            title: 'ステータス',
            dataIndex: 'status',
            key: 'status',
            width: 140,
            render: (_: string, record: BentoOrder) => getStatusBadge(record)
        },
        {
            title: '受取時間',
            dataIndex: 'pickup_time',
            key: 'pickupTime',
            width: 80,
            render: (time: string) => (
                <Tag color="blue" icon={<Clock size={12} />}>
                    {time}
                </Tag>
            )
        },
        {
            title: 'お客様',
            dataIndex: 'customer_name',
            key: 'customerName',
            width: 100,
            render: (name: string, record: BentoOrder) => (
                <div>
                    <div>{name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{record.phone}</div>
                </div>
            )
        },
        {
            title: '注文内容',
            dataIndex: 'items',
            key: 'items',
            render: (items: any[]) => (
                <div>
                    {items.map((item, index) => (
                        <div key={index} style={{ fontSize: '12px' }}>
                            {item.name} ×{item.quantity}
                        </div>
                    ))}
                </div>
            )
        },
        {
            title: '数量',
            dataIndex: 'quantity_total',
            key: 'quantityTotal',
            width: 60,
            align: 'center' as const,
            render: (qty: number) => (
                <Badge count={qty} style={{ backgroundColor: '#52c41a' }} />
            )
        },
        {
            title: '金額',
            dataIndex: 'total_amount',
            key: 'totalAmount',
            width: 80,
            align: 'right' as const,
            render: (amount: number) => amount ? `¥${amount.toLocaleString()}` : '¥0'
        },
        {
            title: '操作',
            key: 'actions',
            width: 200,
            render: (_: any, record: BentoOrder) => (
                <Space direction="vertical" size="small">
                    {/* 受取済みバッジ */}
                    {(record as any).picked_up === 1 && (
                        <Tag color="green" icon={<CheckCircle size={14} />}>
                            ✅ 受取済
                        </Tag>
                    )}

                    {/* 操作ボタン */}
                    <Space>
                        {(() => {
                            const displayStatus = getDisplayStatus(record);
                            const orderId = getOrderId(record);

                            if (displayStatus === 'pending') {
                                return (
                                    <>
                                        <Button
                                            size="small"
                                            icon={<Printer size={14} />}
                                            onClick={() => handlePrintAndConfirm(orderId)}
                                        >
                                            印刷して受付
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                setSelectedOrder(record);
                                                setDetailModalVisible(true);
                                            }}
                                        >
                                            詳細
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            onClick={() => handleStatusUpdate(orderId, 'cancelled')}
                                        >
                                            キャンセル
                                        </Button>
                                    </>
                                );
                            }

                            if (displayStatus === 'confirmed') {
                                return (
                                    <>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                console.log('Button Clicked!', record);
                                                handleManualPickup(orderId);
                                            }}
                                        >
                                            🤲 手動で受取済
                                        </Button>
                                        <Button
                                            size="small"
                                            onClick={() => {
                                                setSelectedOrder(record);
                                                setDetailModalVisible(true);
                                            }}
                                        >
                                            詳細
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            onClick={() => handleStatusUpdate(orderId, 'cancelled')}
                                        >
                                            キャンセル
                                        </Button>
                                    </>
                                );
                            }

                            return (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setSelectedOrder(record);
                                        setDetailModalVisible(true);
                                    }}
                                >
                                    詳細
                                </Button>
                            );
                        })()}
                    </Space>
                </Space>
            )
        }
    ];

    // 次の受取時間までの時間
    const getTimeUntilNext = () => {
        if (!stats?.nextPickupTime) return null;
        
        const now = new Date();
        const [hours, minutes] = stats.nextPickupTime.split(':').map(Number);
        const pickupTime = new Date();
        pickupTime.setHours(hours, minutes, 0, 0);
        
        const diff = pickupTime.getTime() - now.getTime();
        if (diff < 0) return '過ぎています';
        
        const diffMinutes = Math.floor(diff / 60000);
        const diffHours = Math.floor(diffMinutes / 60);
        const remainMinutes = diffMinutes % 60;
        
        if (diffHours > 0) {
            return `${diffHours}時間${remainMinutes}分`;
        }
        return `${remainMinutes}分`;
    };

    return (
        <PageContainer>
            <style>{`
                @keyframes bento-blink {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                }
            `}</style>
            <div>
            {/* ヘッダー */}
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Button
                        type="primary"
                        icon={<Home size={16} />}
                        onClick={() => navigate('/home')}
                        style={{ backgroundColor: '#1890ff' }}
                    >
                        ホーム
                    </Button>
                    <h1 style={{ margin: 0, fontSize: '24px' }}>
                        🍱 弁当予約管理
                    </h1>
                </div>
                <Space>
                    <span>自動更新</span>
                    <Switch
                        checked={autoRefresh}
                        onChange={setAutoRefresh}
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                    />
                    <Button
                        icon={<RefreshCw size={16} />}
                        onClick={fetchData}
                        loading={loading}
                    >
                        更新
                    </Button>
                    <Button
                        type="primary"
                        icon={<Printer size={16} />}
                        onClick={handleMorningPrint}
                        disabled={new Date().getHours() >= 10}
                    >
                        朝の一括印刷
                    </Button>
                </Space>
            </div>


            {/* 本日の開店時間設定 */}
            <Card
                title="本日の開店時間"
                style={{ marginBottom: '20px' }}
                extra={
                    <Tooltip title="出張販売などで開店時間が変わる場合に設定してください">
                        <AlertCircle size={16} style={{ color: '#999' }} />
                    </Tooltip>
                }
            >
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                        <Space>
                            <span style={{ fontWeight: 500 }}>開店時刻:</span>
                            <TimePicker
                                value={dayjs(todayOpenTime, 'HH:mm')}
                                onChange={(time) => {
                                    if (time) {
                                        handleOpenTimeChange(time.format('HH:mm'));
                                    }
                                }}
                                format="HH:mm"
                                minuteStep={30}
                                showNow={false}
                                style={{ width: 120 }}
                                disabledHours={() => {
                                    // 10時以前と22時以降を無効化
                                    return [...Array(10).keys(), ...Array(2).keys().map(i => i + 22)];
                                }}
                                placeholder="時刻選択"
                            />
                            {todayOpenTime !== '11:00' && (
                                <Tag color="orange" style={{ marginLeft: 8 }}>
                                    通常は11:00開店
                                </Tag>
                            )}
                        </Space>
                    </div>

                    {todayOpenTime !== '11:00' && (
                        <Alert
                            type="warning"
                            message={`本日は${todayOpenTime}開店に設定されています（通常営業時間: 11:00-14:00, 17:00-20:00）`}
                            description="30分単位で11:00～21:00まで設定できます。設定は翌日自動的に11:00にリセットされます。"
                            showIcon
                        />
                    )}

                    {todayOpenTime === '11:00' && (
                        <Alert
                            type="info"
                            message="通常営業時間で運営中"
                            description="出張販売や特別な事情で開店時刻を変更する場合は、上記の時刻選択で設定してください。"
                            showIcon
                        />
                    )}
                </Space>
            </Card>
            {/* 統計カード */}
            {stats && (
                <Row gutter={16} style={{ marginBottom: '20px' }}>
                    <Col span={4}>
                        <Card>
                            <Statistic
                                title="本日の予約"
                                value={stats.totalOrders}
                                suffix="件"
                                prefix={<Package />}
                            />
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card>
                            <Statistic
                                title="合計数量"
                                value={stats.totalQuantity}
                                suffix="個"
                                prefix={<Coffee />}
                            />
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card>
                            <Statistic
                                title="売上金額"
                                value={stats.totalAmount}
                                prefix="¥"
                                precision={0}
                            />
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card>
                            <Statistic
                                title="未処理"
                                value={stats.statusBreakdown.pending}
                                suffix="件"
                                valueStyle={{ color: '#faad14' }}
                            />
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card>
                            <Statistic
                                title="調理中"
                                value={stats.statusBreakdown.preparing}
                                suffix="件"
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card>
                            <Statistic
                                title="次の受取"
                                value={stats.nextPickupTime || '---'}
                                suffix={getTimeUntilNext() ? `(${getTimeUntilNext()})` : ''}
                                valueStyle={{ fontSize: '20px' }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* 予約一覧 */}
            <Card title={`🍱 本日の弁当予約 (${orders.length}件)`}>
                <div style={{ marginBottom: 16 }}>
                    <Space size="small">
                        <Tag color="default">全 {orders.length} 件</Tag>
                        <Tag color="red">未受取 {orders.filter((o: any) => !o.picked_up).length} 件</Tag>
                        <Tag color="green">受取済 {orders.filter((o: any) => o.picked_up === 1).length} 件</Tag>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={(() => {
                        const data = getFilteredOrders();
                        console.log('Table dataSource:', data);
                        console.log('Table dataSource type:', typeof data);
                        console.log('Is Array:', Array.isArray(data));
                        return data;
                    })()}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showTotal: (total) => `全 ${total} 件`
                    }}
                    rowClassName={(record) => {
                        const displayStatus = getDisplayStatus(record);
                        if (displayStatus === 'pending') return 'pending-row';
                        return '';
                    }}
                />
            </Card>

                    {/* 詳細モーダル */}
                    <Modal
                        title="予約詳細"
                        open={detailModalVisible}
                        onCancel={() => setDetailModalVisible(false)}
                        footer={null}
                    >
                        {selectedOrder ? (
                            <div style={{ lineHeight: 1.8 }}>
                                <div><strong>予約番号:</strong> {(selectedOrder as any).order_number || selectedOrder.orderNumber}</div>
                                <div><strong>お客様:</strong> {(selectedOrder as any).customer_name || selectedOrder.customerName}</div>
                                <div><strong>電話番号:</strong> {selectedOrder.phone}</div>
                                <div><strong>受取時間:</strong> {(selectedOrder as any).pickup_time || selectedOrder.pickupTime}</div>
                                <div><strong>ステータス:</strong> {selectedOrder.status}</div>
                                <div><strong>数量:</strong> {selectedOrder.quantityTotal ?? (selectedOrder as any).quantity_total ?? 0}</div>
                                <div>
                                    <strong>金額:</strong> {(() => {
                                        const amount = selectedOrder.totalAmount ?? (selectedOrder as any).total_amount ?? 0;
                                        return `¥${amount.toLocaleString()}`;
                                    })()}
                                </div>
                                <div><strong>メモ:</strong> {selectedOrder.memo || (selectedOrder as any).order_memo || '-'}</div>
                                <div style={{ marginTop: 12 }}>
                                    <strong>注文内容:</strong>
                                    <ul style={{ paddingLeft: 18, marginTop: 6 }}>
                                        {(selectedOrder.items || []).map((item, idx) => (
                                            <li key={`${item.name}-${idx}`}>{item.name} ×{item.quantity}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        ) : (
                            <div>詳細情報がありません。</div>
                        )}
                    </Modal>

            </div>
        </PageContainer>
    );
};

export default BentoReservationManager;
